import { reportError } from "@cabocheck/shared";
import type { LinksFunction, LoaderFunctionArgs } from "react-router";
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  data,
  isRouteErrorResponse,
  useLoaderData,
  useMatches,
  useRouteError,
} from "react-router";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { useTranslation } from "react-i18next";
import styles from "./tailwind.css?url";
import { useChangeLanguage } from "./utils/useChangeLanguage";
import { getSession } from "./sessions.server";
import { getLocale } from "./utils/locale.server";

export const links: LinksFunction = () => [{ rel: "stylesheet", href: styles }];

function Breadcrumb() {
  const { t } = useTranslation();
  return <Link to="/">{t("Home")}</Link>;
}

export const handle = {
  breadcrumbs: () => [<Breadcrumb key="home" data-mode="is_base" />],
  // In the handle export, we can add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  // TIP: In most cases, you should set this to your defaultNS from your i18n config
  // or if you did not set one, set it to the i18next default namespace "translation"
  i18n: "common",
};

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await getLocale(request);
  const session = await getSession(request.headers.get("Cookie"));
  const auth_token = session.get("auth_token");

  return data({
    faviconHref: getFaviconHref(request),
    loggedIn: !!auth_token,
    locale,
  });
}

function App() {
  const { i18n } = useTranslation();
  const data = useLoaderData<typeof loader>();
  const matches = useMatches();
  const isHome = matches[matches.length - 1]?.pathname === "/";

  // Get the locale from the loader

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  useChangeLanguage(data.locale);

  const isCaboVax = matches[matches.length - 1]?.pathname === "/cabovax";

  return (
    <NuqsAdapter>
      <html lang={data.locale} dir={i18n.dir()} className={isHome ? "scroll-smooth" : undefined}>
        <head>
          <title>CaboCheck - Partner der Apotheken</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <meta name="title" property="og:title" content="CaboCheck - Partner der Apotheken" />
          <meta property="og:image" content="https://cabocheck.fra1.digitaloceanspaces.com/images/CaboCheck-Logo.png" />
          <meta property="og:image:width" content="190" />
          <meta property="og:image:height" content="52" />
          <meta property="og:image:type" content="image/png" />
          <meta
            name="description"
            property="og:description"
            content="Gesundheit beginnt mit einem Test. Jetzt in Ihrer Apotheke."
          />
          <meta
            name="keywords"
            content="Kapillarblut, Kapillarbluttest, Blutuntersuchung in Apotheken, Kapillarblut in Apotheken, Bluttest, Test in Apotheken"
          />
          {!isCaboVax && <link rel="icon" href={data.faviconHref} />}
          <Meta />
          <Links />
        </head>
        <body>
          <Outlet />
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    </NuqsAdapter>
  );
}

function getFaviconHref(request: Request) {
  const url = new URL(request.url);
  const host = url.host;
  if (host.includes("localhost") || host.includes("cabo-local.local")) return "/favicon-local.svg";
  if (host.includes("dev")) return "/favicon-dev.svg";
  return "/favicon.ico";
}

export default App;

export function ErrorBoundary() {
  const error = useRouteError();
  reportError("Application error boundary reached", error);

  function getMessage() {
    try {
      if (isRouteErrorResponse(error) && error.data) {
        if ("message" in error.data) {
          return error.data.message;
        }
      } else if (error instanceof Error) {
        return error.message;
      } else if (import.meta.env.DEV) {
        return JSON.stringify(error);
      }
    } catch {
      return "-";
    }
  }

  return (
    <html className="flex h-screen" lang="de">
      <head>
        <title>Oops!</title>
        <Meta />
        <Links />
      </head>
      <body className="grid flex-1 items-center justify-center text-center">
        <div className="flex flex-col gap-4">
          <h1>
            {isRouteErrorResponse(error)
              ? `${error.status} ${error.statusText}`
              : error instanceof Error
                ? error.message
                : "Unknown Error"}
          </h1>
          <pre>{getMessage()}</pre>
        </div>
        <Scripts />
      </body>
    </html>
  );
}
